import React, { useState, useRef, KeyboardEvent, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { X, Tag } from "lucide-react";
import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { softSkills, hardSkills, getSkillType } from "@/constants/skills";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface SkillsInputProps {
  skills: string[];
  onChange: (skills: string[]) => void;
  placeholder?: string;
  className?: string;
}

export const SkillsInput = ({ 
  skills, 
  onChange, 
  placeholder = "Add a skill...", 
  className 
}: SkillsInputProps) => {
  const [inputValue, setInputValue] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter skills based on input value
  const getFilteredSkills = (query: string) => {
    const lowerQuery = query.toLowerCase();
    
    if (!query) return { hard: [], soft: [] };
    
    return {
      hard: hardSkills.filter(skill => 
        skill.toLowerCase().includes(lowerQuery) && !skills.includes(skill)
      ).slice(0, 5),
      soft: softSkills.filter(skill => 
        skill.toLowerCase().includes(lowerQuery) && !skills.includes(skill)
      ).slice(0, 5)
    };
  };
  
  const filteredSkills = getFilteredSkills(inputValue);
  const hasFilteredSkills = filteredSkills.hard.length > 0 || filteredSkills.soft.length > 0;

  // Open popover when typing
  useEffect(() => {
    if (inputValue) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [inputValue]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Add skill on Enter or comma
    if ((e.key === "Enter" || e.key === ",") && inputValue.trim()) {
      e.preventDefault();
      const newSkill = inputValue.trim();
      if (!skills.includes(newSkill)) {
        onChange([...skills, newSkill]);
      }
      setInputValue("");
    }
    
    // Remove last skill on Backspace if input is empty
    if (e.key === "Backspace" && !inputValue && skills.length > 0) {
      const updatedSkills = [...skills];
      updatedSkills.pop();
      onChange(updatedSkills);
    }
  };

  const addSkill = (skill: string) => {
    if (!skills.includes(skill)) {
      onChange([...skills, skill]);
    }
    setInputValue("");
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const removeSkill = (skillToRemove: string) => {
    onChange(skills.filter(skill => skill !== skillToRemove));
  };

  // Focus the input when clicking anywhere in the container
  const handleContainerClick = () => {
    inputRef.current?.focus();
  };

  // Update badge color based on skill type using light blue colors
  const getBadgeStyle = (skill: string) => {
    const type = getSkillType(skill);
    if (type === "hard") {
      return "bg-blue-sky text-white"; // Using blue-sky from your color palette
    }
    return "bg-blue-muted text-white"; // Using blue-muted from your color palette
  };

  return (
    <div 
      className={cn(
        "flex flex-wrap gap-2 p-2 border border-input rounded-sm min-h-10 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
        className
      )}
      onClick={handleContainerClick}
    >
      {skills.map((skill) => (
        <Badge 
          key={skill} 
          variant="secondary" 
          className={`${getBadgeStyle(skill)} gap-1 h-7`}
        >
          {skill}
          <button 
            type="button" 
            onClick={(e) => {
              e.stopPropagation();
              removeSkill(skill);
            }}
            className="ml-1 hover:bg-black/20 rounded-full p-0.5"
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
      
      <Popover open={open && hasFilteredSkills} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="flex-grow min-w-20">
            <Input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="border-0 p-0 h-7 w-full bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
              placeholder={skills.length === 0 ? placeholder : ""}
            />
          </div>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-60" align="start" onOpenAutoFocus={(e) => {
          e.preventDefault();
        }}>
          <Command>
            <CommandList>
              {filteredSkills.hard.length > 0 && (
                <CommandGroup heading="Technical Skills">
                  {filteredSkills.hard.map((skill) => (
                    <CommandItem 
                      key={skill} 
                      onSelect={() => addSkill(skill)}
                      className="flex items-center gap-2 cursor-pointer"
                      onFocus={(e) => e.preventDefault()}
                    >
                      <Tag className="h-3.5 w-3.5 text-blue-sky" />
                      {skill}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              
              {filteredSkills.soft.length > 0 && (
                <CommandGroup heading="Soft Skills">
                  {filteredSkills.soft.map((skill) => (
                    <CommandItem 
                      key={skill} 
                      onSelect={() => addSkill(skill)}
                      className="flex items-center gap-2 cursor-pointer"
                      onFocus={(e) => e.preventDefault()}
                    >
                      <Tag className="h-3.5 w-3.5 text-blue-muted" />
                      {skill}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              
              {inputValue.trim() && (
                <CommandGroup heading="Custom">
                  <CommandItem 
                    onSelect={() => addSkill(inputValue.trim())}
                    className="flex items-center gap-2 cursor-pointer"
                    onFocus={(e) => e.preventDefault()}
                  >
                    <Tag className="h-3.5 w-3.5 text-blue-soft" />
                    Add "{inputValue.trim()}"
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
